/**
 * The ability catalog: abilities as inspectable typed data (PRD #784, slice A).
 *
 * An ability declares three separable things.
 * `shape` answers "who is affected", `payloads` answer "what happens to them",
 * and the presentation descriptor carries name, clips, and effect ids that
 * resolution never reads. Cone plus damage is Crescent Sweep, line plus damage
 * is Piercing Thrust, self plus apply-status is Aegis, dash plus damage plus
 * knockback is Charge.
 *
 * The default export surface is presentation-free. {@link ability} and
 * {@link abilityCatalog} both hand out an {@link AbilityDefinition}, which has
 * no presentation member at all, so resolution code cannot reach a clip id even
 * by taking the shortest available call. Presentation is reachable only through
 * {@link abilityPresentation}, which the wire and client edge may call and
 * nothing in `src/sim/combat/` may. An import guard in the test file pins the
 * list of modules allowed to name it.
 *
 * This module is pure data plus validation. It owns no runtime state, and
 * nothing consumes the catalog yet: the runtime, the statuses registry, and the
 * Warrior migration land in later slices.
 */

import { brain } from './brains';
import { DAMAGE_TYPES, type DamageType } from './damageTypes';
import { status, statusCanDamage, statusCatalog, type StatusCatalog } from './statuses';

/**
 * The element union lives in `damageTypes.ts` since PRD #804, because the
 * status registry consumes it too and imports must stay acyclic. It is
 * re-exported here because PRD #803 declared it here and every consumer
 * reaches for it here.
 */
export { DAMAGE_TYPES } from './damageTypes';
export type { DamageType } from './damageTypes';

/** A stable ability id. Ids are content identity and never change once shipped. */
export type AbilityId = string;

/** A free-form property tag, so later work can target abilities by property. */
export type AbilityTag = string;

/** The team that may cast an ability. Heroes are `player`, everything else is not. */
export type CombatantTeam = 'player' | 'monster';

/**
 * What happens to an ability's pending effects when its caster dies mid-cast.
 * `cancel` drops them, `persist` lets them run on their own clock, and
 * `resolve-immediately` resolves them at the moment of death.
 */
export type CasterDeathPolicy = 'cancel' | 'persist' | 'resolve-immediately';

export const CASTER_DEATH_POLICIES: readonly CasterDeathPolicy[] = [
  'cancel',
  'persist',
  'resolve-immediately',
];

/**
 * How an ability can be answered.
 *
 * The law is that hostile damage must be answerable, not that hostile damage
 * must be slow. An arrow is answered by dodging the arrow and a swing is
 * answered by not standing in reach, so a mode is a promise about where the
 * answer lives rather than a speed class. Each one carries its own obligation,
 * enforced by {@link validateAbility}:
 *
 * - `instant` offers no answer at all and is therefore player-only.
 * - `melee` puts the answer in proximity, so its shape may not out-reach
 *   {@link MELEE_CONTACT_RADIUS}.
 * - `projectile` puts the answer in the travelling object, so it must be the
 *   `projectile` shape and may not out-run {@link MAX_REACTABLE_PROJECTILE_SPEED}.
 * - `telegraphed` puts the answer in the announced area, so it must lead by at
 *   least {@link MINIMUM_TELEGRAPH_TICKS}.
 */
export type CounterplayMode = 'instant' | 'melee' | 'projectile' | 'telegraphed';

export const COUNTERPLAY_MODES: readonly CounterplayMode[] = [
  'instant',
  'melee',
  'projectile',
  'telegraphed',
];

/**
 * The shared minimum wind-up for a telegraphed ability, in 50ms simulation
 * ticks. Half a second is the shortest lead that leaves a real dodge decision.
 */
export const MINIMUM_TELEGRAPH_TICKS = 10;

/**
 * The furthest from its caster a `melee` ability may select, in tiles.
 *
 * Three tiles is the longest melee reach the game authors today, the Piercing
 * Thrust lane, and a player at the dungeon run speed of 8 tiles per second
 * clears it in well under the half second a telegraph leads by. Anything longer
 * stops being answerable by proximity and owes the player a different answer.
 */
export const MELEE_CONTACT_RADIUS = 3;

/**
 * The fastest a `projectile` ability may travel, in tiles per second.
 *
 * The counterplay is stepping out of the line, so the shot has to leave time to
 * do it. At twelve tiles per second a quarter second of human reaction costs
 * three tiles of flight, which keeps a shot taken at ordinary engagement range
 * inside the same half-second answering window {@link MINIMUM_TELEGRAPH_TICKS}
 * grants, while a player moving at the 8 tiles per second run speed clears their
 * own width in a fraction of it.
 */
export const MAX_REACTABLE_PROJECTILE_SPEED = 12;

/** Resources an ability consumes on cast. */
export interface AbilityCosts {
  mana: number;
}

/** Delivery geometry. Each variant declares only its own parameters. */
export type AbilityShape =
  | { kind: 'self' }
  | { kind: 'cone'; rangeTiles: number; arcDegrees: number }
  | { kind: 'circle'; radiusTiles: number; metric: 'chebyshev' | 'euclidean' }
  | { kind: 'line'; rangeTiles: number; widthTiles: number }
  | {
      kind: 'dash';
      distanceTiles: number;
      durationMs: number;
      contactRadiusTiles: number;
    }
  | {
      kind: 'projectile';
      rangeTiles: number;
      speedTilesPerS: number;
      /**
       * Combatants past the first this shot punches through before it stops.
       * Absent is a single-target shot (PRD #800).
       *
       * A parameter of the existing shape rather than a new shape: an arrow
       * that passes through a rank is the same delivery answering the same
       * counterplay, and expressing "a volley" as three catalog entries or as a
       * bespoke shape would put content in the kernel.
       */
      pierceTargets?: number;
    }
  | {
      kind: 'tile';
      radiusTiles: number;
      /**
       * Several impacts from one cast, each with its own footprint and its own
       * impact tick (PRD #793).
       *
       * A Meteor Shower is one decision, not five: the caster commits once, and
       * the pattern that falls out of it is frozen with the aim. Expressing it
       * as a parameter of the `tile` shape rather than as five catalog entries
       * or a bespoke shape keeps the schema honest - every impact is still a
       * disc at a frozen point, and the telegraph law still binds each one,
       * because each announces its own footprint at cast start.
       *
       * `count` includes the impact at the aimed point, which is always first
       * and always exactly where the caster aimed. `spreadTiles` bounds how far
       * the remaining impacts scatter from it, and `staggerTicks` is the gap
       * between consecutive impacts.
       */
      impacts?: {
        count: number;
        spreadTiles: number;
        staggerTicks: number;
      };
    };

/**
 * What a summoned combatant does when its owner is removed (PRD #804).
 * `dissipate` removes the summon with its owner, and `linger` lets it run out
 * its own declared lifetime.
 */
export type SummonOwnerDeathPolicy = 'dissipate' | 'linger';

export const SUMMON_OWNER_DEATH_POLICIES: readonly SummonOwnerDeathPolicy[] = [
  'dissipate',
  'linger',
];

/**
 * What happens to every combatant the shape selected. Payloads are an ordered
 * list applied in declaration order, to targets in stable entity-id order.
 */
export type AbilityPayload =
  | {
      kind: 'damage';
      weaponPercent: number;
      flat: number;
      /**
       * The wheel entry resolution reads the target's resist for, and which
       * selects the caster's damage scalar: physical is Strength's job and
       * every other type is Intelligence's (PRD #792).
       */
      damageType: DamageType;
    }
  | {
      kind: 'applyStatus';
      statusId: string;
      durationTicks: number;
      magnitude: number;
    }
  | { kind: 'knockback'; distanceTiles: number; direction: 'away' | 'aside' }
  | { kind: 'heal'; weaponPercent: number; flat: number }
  | { kind: 'resource'; resource: 'mana'; amount: number }
  | {
      /**
       * Bring combatants into existence on the caster's team (PRD #804).
       *
       * The first payload that creates a combatant rather than acting on one a
       * shape selected. Ownership, lifetime, and the owner-death policy are
       * declared here once and enforced by the runtime, so no ability carries
       * pet code. The runtime asks its caller to materialize each body, because
       * what a summon looks like and where it may stand belong to the world the
       * encounter runs in, not to the kernel.
       */
      kind: 'summon';
      /** The monster archetype the summon is built from, by stable id. */
      archetypeId: string;
      /** The brain that drives it, resolved against the kernel registry. */
      brainId: string;
      /** How many combatants one cast creates. */
      count: number;
      /** Ticks a summon lives. Finite by law: no summon is forever. */
      lifetimeTicks: number;
      /**
       * The per-caster field budget for this archetype. Summoning at the cap
       * replaces the oldest summon rather than refusing the cast, so the
       * ability stays worth pressing and the budget stays a budget.
       */
      maxAlive: number;
      ownerDeath: SummonOwnerDeathPolicy;
    };

/** Clip selection, optionally conditional on the caster's weapon grip. */
export type AbilityClipSelection =
  | { kind: 'fixed'; clipId: string }
  | { kind: 'grip'; oneHanded: string; twoHanded: string };

/**
 * Presentation descriptor: the player-facing name, the clip, and the effect
 * ids. Callers translate this at their own edge and resolution never sees it.
 */
export interface AbilityPresentation {
  /** The player-facing ability name, rendered by the combat HUD. */
  name: string;
  clip?: AbilityClipSelection;
  effectIds: readonly string[];
  /**
   * The art a shot in flight draws with, for an ability that puts one in the
   * air. Cosmetic like every other member here: the kernel simulates the
   * flight and never learns what it looks like, and the effect stream turns
   * this into the projectile entity the client renders.
   */
  projectile?: string;
}

/**
 * The mechanical ability definition, which is what every consumer other than
 * the wire and client edge gets. There is no presentation member here by
 * construction, not by convention.
 */
export interface AbilityDefinition {
  id: AbilityId;
  /** Teams allowed to cast this ability. Drives the counterplay law. */
  casters: readonly CombatantTeam[];
  costs: AbilityCosts;
  cooldownTicks: number;
  counterplay: CounterplayMode;
  castTicks: number;
  recoveryTicks: number;
  /** What happens to pending effects when the caster dies mid-cast. */
  casterDeath: CasterDeathPolicy;
  shape: AbilityShape;
  payloads: readonly AbilityPayload[];
  tags: readonly AbilityTag[];
  /** Ability ids that cannot share a loadout with this one. Declared mutually. */
  incompatibleWith: readonly AbilityId[];
}

/**
 * An authored catalog entry: the mechanical definition plus the presentation
 * descriptor. This shape exists for authoring and validation. Only the two
 * accessors below hand data out, and only one of them exposes presentation.
 */
export interface AuthoredAbility extends AbilityDefinition {
  presentation: AbilityPresentation;
}

export type AbilityCatalog = ReadonlyMap<AbilityId, AbilityDefinition>;

const ID_PATTERN = /^[a-z0-9]+(?:[-.][a-z0-9]+)*$/;

function isNonNegativeInteger(value: unknown): boolean {
  return Number.isSafeInteger(value) && (value as number) >= 0;
}

function isPositiveNumber(value: unknown): boolean {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function isNonNegativeNumber(value: unknown): boolean {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function hasDuplicates(values: readonly string[]): boolean {
  return new Set(values).size !== values.length;
}

function stringArray(value: unknown): readonly string[] | null {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string')
    ? (value as readonly string[])
    : null;
}

function validateShape(shape: AbilityShape): string[] {
  const problems: string[] = [];
  if (shape === null || typeof shape !== 'object') return ['shape must be declared'];
  const positive = (name: string, value: number) => {
    if (!isPositiveNumber(value)) {
      problems.push(`shape ${shape.kind} ${name} must be a positive number`);
    }
  };
  switch (shape.kind) {
    case 'self':
      break;
    case 'cone':
      positive('rangeTiles', shape.rangeTiles);
      positive('arcDegrees', shape.arcDegrees);
      if (shape.arcDegrees > 360) problems.push('shape cone arcDegrees must be at most 360');
      break;
    case 'circle':
      positive('radiusTiles', shape.radiusTiles);
      if (shape.metric !== 'chebyshev' && shape.metric !== 'euclidean') {
        problems.push('shape circle metric must be chebyshev or euclidean');
      }
      break;
    case 'line':
      positive('rangeTiles', shape.rangeTiles);
      positive('widthTiles', shape.widthTiles);
      break;
    case 'dash':
      positive('distanceTiles', shape.distanceTiles);
      positive('durationMs', shape.durationMs);
      positive('contactRadiusTiles', shape.contactRadiusTiles);
      break;
    case 'projectile':
      positive('rangeTiles', shape.rangeTiles);
      positive('speedTilesPerS', shape.speedTilesPerS);
      if (
        shape.pierceTargets !== undefined &&
        !isNonNegativeInteger(shape.pierceTargets)
      ) {
        problems.push('shape projectile pierceTargets must be a non-negative integer');
      }
      break;
    case 'tile': {
      positive('radiusTiles', shape.radiusTiles);
      const impacts = shape.impacts;
      if (impacts !== undefined) {
        if (impacts === null || typeof impacts !== 'object') {
          problems.push('shape tile impacts must be an object');
          break;
        }
        // A count below two is a single impact wearing a pattern's clothes, and
        // it would leave the shape with two ways to say the same thing.
        if (!isNonNegativeInteger(impacts.count) || impacts.count < 2) {
          problems.push('shape tile impacts count must be an integer of at least 2');
        }
        positive('impacts spreadTiles', impacts.spreadTiles);
        // A stagger of zero would land every impact on one tick, which is one
        // wide impact authored the hard way.
        if (!isNonNegativeInteger(impacts.staggerTicks) || impacts.staggerTicks === 0) {
          problems.push('shape tile impacts staggerTicks must be a positive integer');
        }
      }
      break;
    }
    default: {
      const exhaustive: never = shape;
      problems.push(`unknown shape ${JSON.stringify(exhaustive)}`);
    }
  }
  return problems;
}

/**
 * How far from its caster a shape can select, in tiles, or null when the shape
 * is not anchored on the caster at all.
 *
 * A `tile` shape is placed at an aimed point rather than measured from the
 * caster, so it has no contact reach to compare against the melee radius, and
 * answering null is what lets the melee obligation reject it by name instead of
 * silently passing it on a distance nobody computed.
 */
function shapeContactReachTiles(shape: AbilityShape): number | null {
  if (shape === null || typeof shape !== 'object') return null;
  switch (shape.kind) {
    case 'self':
      return 0;
    case 'cone':
      return shape.rangeTiles;
    case 'circle':
      return shape.radiusTiles;
    case 'line':
      return shape.rangeTiles;
    // A dash carries its contact radius with it to the end of the rush, so its
    // reach is the whole traversal rather than the radius alone.
    case 'dash':
      return shape.distanceTiles + shape.contactRadiusTiles;
    case 'projectile':
      return shape.rangeTiles;
    default:
      return null;
  }
}

/**
 * Whether the shape itself is the effect, so an ability using it may declare no
 * payloads at all.
 *
 * Only the dash qualifies today: it moves its caster, which is a gameplay
 * outcome no payload expresses. Every other shape merely answers "who", so an
 * empty payload list under one of them selects a target set and does nothing
 * with it.
 */
function shapeActsAlone(shape: AbilityShape): boolean {
  return shape !== null && typeof shape === 'object' && shape.kind === 'dash';
}

/**
 * How a summon payload's content references are resolved (PRD #804).
 *
 * The brain registry is the kernel's own, so it is the default. Archetypes are
 * content declared outside the kernel, so the guard that validates authored
 * entries injects the real registry here; with no resolver supplied the
 * archetype id is checked for shape only.
 */
export interface SummonResolvers {
  hasArchetype?: (id: string) => boolean;
  hasBrain?: (id: string) => boolean;
}

function validatePayload(
  payload: AbilityPayload,
  index: number,
  statuses: StatusCatalog,
  summons: SummonResolvers,
): string[] {
  const problems: string[] = [];
  if (payload === null || typeof payload !== 'object') {
    return [`payload ${index} must be an object`];
  }
  const nonNegative = (name: string, value: number) => {
    if (!isNonNegativeNumber(value)) {
      problems.push(`payload ${index} ${payload.kind} ${name} must be a non-negative number`);
    }
  };
  switch (payload.kind) {
    case 'damage':
    case 'heal':
      nonNegative('weaponPercent', payload.weaponPercent);
      nonNegative('flat', payload.flat);
      if (payload.weaponPercent === 0 && payload.flat === 0) {
        problems.push(`payload ${index} ${payload.kind} does nothing`);
      }
      // Damage only. The wheel describes what a hit is made of, and a resist
      // that turned away a heal would be a different mechanic wearing the same
      // field, so healing stays untyped.
      if (payload.kind === 'damage' && !DAMAGE_TYPES.includes(payload.damageType)) {
        problems.push(
          `payload ${index} damage damageType must be one of ${DAMAGE_TYPES.join(', ')}`,
        );
      }
      break;
    case 'applyStatus':
      // Shape is not enough. A misspelled status id is a stable id by pattern,
      // so without a registry lookup it validates clean: a monster ability is
      // then merely force-telegraphed by the fail-closed predicate below, and a
      // player ability silently no-ops at runtime with nothing reporting it.
      // Checking membership here makes fail-closed a backstop rather than the
      // thing catching authoring mistakes.
      if (typeof payload.statusId !== 'string' || !ID_PATTERN.test(payload.statusId)) {
        problems.push(`payload ${index} applyStatus statusId must be a stable id`);
      } else if (!status(payload.statusId, statuses)) {
        problems.push(
          `payload ${index} applyStatus statusId ${payload.statusId} is not in the status registry`,
        );
      }
      if (!isNonNegativeInteger(payload.durationTicks) || payload.durationTicks === 0) {
        problems.push(`payload ${index} applyStatus durationTicks must be a positive integer`);
      }
      nonNegative('magnitude', payload.magnitude);
      break;
    case 'knockback':
      if (!isPositiveNumber(payload.distanceTiles)) {
        problems.push(`payload ${index} knockback distanceTiles must be a positive number`);
      }
      if (payload.direction !== 'away' && payload.direction !== 'aside') {
        problems.push(`payload ${index} knockback direction must be away or aside`);
      }
      break;
    case 'resource':
      if (payload.resource !== 'mana') {
        problems.push(`payload ${index} resource must name a known resource`);
      }
      if (typeof payload.amount !== 'number' || !Number.isFinite(payload.amount)) {
        problems.push(`payload ${index} resource amount must be a finite number`);
      }
      break;
    case 'summon': {
      // The catalog guard's obligation (PRD #804): a summon must resolve its
      // archetype and brain, declare a finite lifetime and a cap, and declare
      // an owner-death policy. A dangling reference here is a combatant that
      // silently never appears, which is exactly the failure mode the
      // applyStatus membership check exists to catch.
      if (typeof payload.archetypeId !== 'string' || !ID_PATTERN.test(payload.archetypeId)) {
        problems.push(`payload ${index} summon archetypeId must be a stable id`);
      } else if (summons.hasArchetype && !summons.hasArchetype(payload.archetypeId)) {
        problems.push(
          `payload ${index} summon archetypeId ${payload.archetypeId} does not resolve to an archetype`,
        );
      }
      const hasBrain = summons.hasBrain ?? ((id: string) => brain(id) !== null);
      if (typeof payload.brainId !== 'string' || !ID_PATTERN.test(payload.brainId)) {
        problems.push(`payload ${index} summon brainId must be a stable id`);
      } else if (!hasBrain(payload.brainId)) {
        problems.push(
          `payload ${index} summon brainId ${payload.brainId} does not resolve to a brain`,
        );
      }
      if (!isNonNegativeInteger(payload.count) || payload.count === 0) {
        problems.push(`payload ${index} summon count must be a positive integer`);
      }
      if (!isNonNegativeInteger(payload.lifetimeTicks) || payload.lifetimeTicks === 0) {
        problems.push(`payload ${index} summon lifetimeTicks must be a positive integer`);
      }
      if (!isNonNegativeInteger(payload.maxAlive) || payload.maxAlive === 0) {
        problems.push(`payload ${index} summon maxAlive must be a positive integer`);
      } else if (
        isNonNegativeInteger(payload.count) &&
        (payload.count as number) > payload.maxAlive
      ) {
        // A cast that overflows its own cap replaces summons it created one
        // line earlier, which is a count wearing a cap's clothes.
        problems.push(`payload ${index} summon count must not exceed maxAlive`);
      }
      if (!SUMMON_OWNER_DEATH_POLICIES.includes(payload.ownerDeath)) {
        problems.push(
          `payload ${index} summon ownerDeath must be one of ${SUMMON_OWNER_DEATH_POLICIES.join(', ')}`,
        );
      }
      break;
    }
    default: {
      const exhaustive: never = payload;
      problems.push(`unknown payload ${JSON.stringify(exhaustive)}`);
    }
  }
  return problems;
}

/**
 * Validate one authored ability and return every problem found, so a guard
 * reports the whole picture rather than the first failure. The posture is
 * defensive throughout: a malformed hand-authored entry is reported, never
 * thrown on, because this function is the thing that catches bad authoring.
 *
 * The counterplay law lives here: an ability that can damage a player, meaning
 * one carrying a `damage` payload and castable by a non-player team, must offer
 * an answer, and whichever answer it declares must satisfy that mode's own
 * obligation. See {@link CounterplayMode}.
 *
 * `catalog` supplies the peers used to prove that declared incompatibilities
 * are mutual, and `statuses` supplies the registry an `applyStatus` payload must
 * name. Both default to the shipped registries, and both are injectable so an
 * ability can be validated against synthetic content. `summons` resolves a
 * summon payload's archetype and brain the same way (PRD #804).
 */
export function validateAbility(
  definition: AuthoredAbility,
  catalog: AbilityCatalog = abilityCatalog,
  statuses: StatusCatalog = statusCatalog,
  summons: SummonResolvers = {},
): string[] {
  const problems: string[] = [];
  if (definition === null || typeof definition !== 'object') {
    return ['ability must be an object'];
  }
  if (typeof definition.id !== 'string' || !ID_PATTERN.test(definition.id)) {
    problems.push('id must be a non-empty stable string');
  }

  const casters = stringArray(definition.casters);
  if (!casters) {
    problems.push('casters must be an array of team names');
  } else if (casters.length === 0) {
    problems.push('casters must declare at least one team');
  } else if (hasDuplicates(casters)) {
    problems.push('casters must be unique');
  } else if (casters.some((team) => team !== 'player' && team !== 'monster')) {
    problems.push('casters must name known teams');
  }

  if (
    definition.costs === null ||
    typeof definition.costs !== 'object' ||
    !isNonNegativeInteger(definition.costs.mana)
  ) {
    problems.push('costs.mana must be a non-negative integer');
  }
  if (!isNonNegativeInteger(definition.cooldownTicks)) {
    problems.push('cooldownTicks must be a non-negative integer');
  }
  if (!isNonNegativeInteger(definition.castTicks)) {
    problems.push('castTicks must be a non-negative integer');
  }
  if (!isNonNegativeInteger(definition.recoveryTicks)) {
    problems.push('recoveryTicks must be a non-negative integer');
  }
  if (!CASTER_DEATH_POLICIES.includes(definition.casterDeath)) {
    problems.push(
      `casterDeath must be one of ${CASTER_DEATH_POLICIES.join(', ')}`,
    );
  }

  problems.push(...validateShape(definition.shape));

  const payloads = Array.isArray(definition.payloads) ? definition.payloads : null;
  if (!payloads) {
    problems.push('payloads must be an array');
  } else if (payloads.length === 0 && !shapeActsAlone(definition.shape)) {
    // An ability that selects targets and does nothing to them is an authoring
    // mistake. A dash is the exception, and the exception is a property of the
    // shape rather than a licence: the traversal *is* the effect, so Disengage
    // and Blink are complete abilities with no payload at all (PRD #800).
    problems.push('payloads must declare at least one entry');
  } else {
    payloads.forEach((payload, index) => {
      problems.push(...validatePayload(payload, index, statuses, summons));
    });
    // A summon acts on its caster, not on a selected target set: applying it
    // through a targeting shape would spawn one field per body caught, which
    // no declared cap could make legible. The self shape is the anchor.
    if (
      payloads.some((payload) => payload?.kind === 'summon') &&
      definition.shape?.kind !== 'self'
    ) {
      problems.push('a summon payload requires the self shape');
    }
  }

  const tags = stringArray(definition.tags);
  if (!tags) {
    problems.push('tags must be an array of strings');
  } else {
    if (hasDuplicates(tags)) problems.push('tags must be unique');
    if (tags.some((tag) => tag.length === 0)) {
      problems.push('tags must be non-empty strings');
    }
  }

  const incompatibleWith = stringArray(definition.incompatibleWith);
  if (!incompatibleWith) {
    problems.push('incompatibleWith must be an array of ability ids');
  } else {
    if (incompatibleWith.includes(definition.id)) {
      problems.push('incompatibleWith must not list the ability itself');
    }
    if (hasDuplicates(incompatibleWith)) {
      problems.push('incompatibleWith must be unique');
    }
    for (const other of incompatibleWith) {
      if (other === definition.id) continue;
      const peer = catalog.get(other);
      if (!peer) {
        problems.push(`incompatibleWith names ${other}, which is not in the catalog`);
      } else if (!peer.incompatibleWith.includes(definition.id)) {
        problems.push(`incompatibility with ${other} must be declared mutually`);
      }
    }
  }

  const presentation = definition.presentation;
  if (presentation === null || typeof presentation !== 'object') {
    problems.push('presentation must be declared');
  } else {
    if (typeof presentation.name !== 'string' || presentation.name.length === 0) {
      problems.push('presentation name must be a non-empty string');
    }
    const effectIds = stringArray(presentation.effectIds);
    if (!effectIds) {
      problems.push('presentation effectIds must be an array of strings');
    } else if (effectIds.some((id) => id.length === 0)) {
      problems.push('presentation effectIds must be non-empty strings');
    }
    if (
      presentation.projectile !== undefined &&
      (typeof presentation.projectile !== 'string' ||
        presentation.projectile.length === 0)
    ) {
      problems.push('presentation projectile must be a non-empty string');
    }
    if (
      presentation.projectile !== undefined &&
      definition.shape?.kind !== 'projectile'
    ) {
      problems.push('presentation projectile belongs to a projectile shape');
    }
  }

  // The damaging predicate asks what an ability does, not what it is called: a
  // payload that applies a damage-over-time status is an act of damage even
  // though its kind is `applyStatus`, so a monster burn has to telegraph like
  // any other hit. The registry answers the question and fails closed on a
  // status nobody has authored yet.
  const damaging = (payloads ?? []).some(
    (payload) =>
      payload?.kind === 'damage' ||
      (payload?.kind === 'applyStatus' && statusCanDamage(payload.statusId, statuses)),
  );
  const hostile = (casters ?? []).some((team) => team !== 'player');
  problems.push(...validateCounterplay(definition, damaging && hostile));
  return problems;
}

/**
 * Enforce the declared counterplay mode's own obligation.
 *
 * Every mode is checked whoever casts it, because a mode is a claim about how
 * the ability can be answered and a claim that is false for a player ability is
 * false for a monster one. `hostileDamage` gates only the one rule that is
 * genuinely about who casts: an ability offering no answer at all may not be
 * pointed at a player.
 */
function validateCounterplay(
  definition: AuthoredAbility,
  hostileDamage: boolean,
): string[] {
  const mode = definition.counterplay;
  if (!COUNTERPLAY_MODES.includes(mode)) {
    return [`counterplay must be one of ${COUNTERPLAY_MODES.join(', ')}`];
  }
  const problems: string[] = [];
  switch (mode) {
    case 'instant': {
      if (definition.castTicks !== 0) {
        problems.push('an instant ability must declare castTicks of 0');
      }
      if (hostileDamage) {
        problems.push(
          `a damaging ability castable by a non-player team must declare counterplay of ${COUNTERPLAY_MODES.filter(
            (other) => other !== 'instant',
          ).join(', ')}`,
        );
      }
      break;
    }
    case 'melee': {
      const reach = shapeContactReachTiles(definition.shape);
      if (reach === null) {
        problems.push(
          'a melee ability must use a shape measured from its caster, which its shape is not',
        );
      } else if (!(reach <= MELEE_CONTACT_RADIUS)) {
        problems.push(
          `a melee ability must select within ${MELEE_CONTACT_RADIUS} tiles of its caster, and this one reaches ${reach}`,
        );
      }
      break;
    }
    case 'projectile': {
      const shape = definition.shape;
      if (shape === null || typeof shape !== 'object' || shape.kind !== 'projectile') {
        problems.push('a projectile ability must use the projectile shape');
      } else if (!(shape.speedTilesPerS <= MAX_REACTABLE_PROJECTILE_SPEED)) {
        problems.push(
          `a projectile ability must travel at most ${MAX_REACTABLE_PROJECTILE_SPEED} tiles per second to stay dodgeable`,
        );
      }
      break;
    }
    case 'telegraphed': {
      if (definition.castTicks < MINIMUM_TELEGRAPH_TICKS) {
        problems.push(
          `a telegraphed ability must wind up for at least ${MINIMUM_TELEGRAPH_TICKS} ticks`,
        );
      }
      break;
    }
    default: {
      const exhaustive: never = mode;
      problems.push(`unknown counterplay ${JSON.stringify(exhaustive)}`);
    }
  }
  return problems;
}

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === 'object') {
    for (const nested of Object.values(value)) deepFreeze(nested);
    Object.freeze(value);
  }
  return value;
}

/**
 * The Warrior abilities, matching today's tuning in `hero.ts` exactly. All five
 * are player-only and instant, which is why the telegraph law does not bind
 * them: the law is conditional on a non-player caster, not blanket.
 *
 * All five declare `resolve-immediately`, which is today's behavior: with no
 * wind-up they resolve within the cast tick, so no pending effect can outlive
 * the caster and the policy is a faithful no-op for them.
 */
const AUTHORED_ABILITIES: readonly AuthoredAbility[] = [
  {
    id: 'warrior-auto-attack',
    casters: ['player'],
    costs: { mana: 0 },
    cooldownTicks: 10,
    counterplay: 'instant',
    castTicks: 0,
    recoveryTicks: 0,
    casterDeath: 'resolve-immediately',
    shape: { kind: 'cone', rangeTiles: 2.5, arcDegrees: 160 },
    payloads: [{ kind: 'damage', weaponPercent: 100, flat: 5, damageType: 'physical' }],
    presentation: {
      name: 'Crescent Sweep',
      clip: { kind: 'fixed', clipId: 'attack-2h' },
      effectIds: ['hero-sweep'],
    },
    tags: ['warrior', 'melee', 'area'],
    incompatibleWith: [],
  },
  {
    id: 'warrior-whirlwind',
    casters: ['player'],
    costs: { mana: 5 },
    cooldownTicks: 8,
    counterplay: 'instant',
    castTicks: 0,
    recoveryTicks: 0,
    casterDeath: 'resolve-immediately',
    shape: { kind: 'circle', radiusTiles: 2.5, metric: 'chebyshev' },
    payloads: [{ kind: 'damage', weaponPercent: 80, flat: 4, damageType: 'physical' }],
    presentation: {
      name: 'Whirlwind',
      clip: { kind: 'fixed', clipId: 'attack-2h' },
      effectIds: ['hero-whirlwind'],
    },
    tags: ['warrior', 'melee', 'area'],
    incompatibleWith: [],
  },
  {
    id: 'warrior-charge',
    casters: ['player'],
    costs: { mana: 0 },
    cooldownTicks: 20,
    counterplay: 'instant',
    castTicks: 0,
    recoveryTicks: 0,
    casterDeath: 'resolve-immediately',
    shape: {
      kind: 'dash',
      distanceTiles: 8,
      durationMs: 220,
      contactRadiusTiles: 0.7,
    },
    payloads: [
      { kind: 'damage', weaponPercent: 125, flat: 10, damageType: 'physical' },
      { kind: 'knockback', distanceTiles: 1, direction: 'aside' },
    ],
    presentation: { name: 'Charge', effectIds: ['hero-charge'] },
    tags: ['warrior', 'melee', 'mobility'],
    incompatibleWith: [],
  },
  {
    id: 'warrior-piercing-thrust',
    casters: ['player'],
    costs: { mana: 3 },
    cooldownTicks: 10,
    counterplay: 'instant',
    castTicks: 0,
    recoveryTicks: 0,
    casterDeath: 'resolve-immediately',
    shape: { kind: 'line', rangeTiles: 3, widthTiles: 0.6 },
    payloads: [{ kind: 'damage', weaponPercent: 130, flat: 6, damageType: 'physical' }],
    presentation: {
      name: 'Piercing Thrust',
      clip: { kind: 'grip', oneHanded: 'thrust-1h', twoHanded: 'thrust-2h' },
      effectIds: ['hero-pierce'],
    },
    tags: ['warrior', 'melee'],
    incompatibleWith: [],
  },
  {
    id: 'warrior-aegis',
    casters: ['player'],
    costs: { mana: 10 },
    cooldownTicks: 200,
    counterplay: 'instant',
    castTicks: 0,
    recoveryTicks: 0,
    casterDeath: 'resolve-immediately',
    shape: { kind: 'self' },
    payloads: [
      {
        kind: 'applyStatus',
        statusId: 'damage-reduction',
        durationTicks: 100,
        magnitude: 50,
      },
    ],
    presentation: {
      name: 'Aegis',
      clip: { kind: 'fixed', clipId: 'spell2' },
      effectIds: ['hero-buff'],
    },
    tags: ['warrior', 'defensive', 'buff'],
    incompatibleWith: [],
  },

  // The Archer (PRD #800). The first real consumer of the `projectile` shape and
  // of the `tile` shape, and the first kit that unlocks across a campaign rather
  // than arriving whole. Physical throughout: the Archer does not touch the
  // elemental wheel.
  {
    id: 'archer-quick-shot',
    casters: ['player'],
    costs: { mana: 0 },
    cooldownTicks: 8,
    // The hero-side entry that proves the mode carries real gameplay: the
    // answer is stepping out of the arrow's line, and it is the same promise a
    // ranged monster will have to make (PRD #793).
    counterplay: 'projectile',
    castTicks: 0,
    recoveryTicks: 0,
    casterDeath: 'resolve-immediately',
    shape: { kind: 'projectile', rangeTiles: 10, speedTilesPerS: 12 },
    payloads: [{ kind: 'damage', weaponPercent: 110, flat: 4, damageType: 'physical' }],
    presentation: {
      name: 'Quick Shot',
      clip: { kind: 'fixed', clipId: 'shoot-bow' },
      effectIds: ['hero-shot'],
      projectile: 'arrow',
    },
    tags: ['archer', 'ranged'],
    incompatibleWith: [],
  },
  {
    id: 'archer-volley',
    casters: ['player'],
    costs: { mana: 8 },
    cooldownTicks: 30,
    counterplay: 'projectile',
    castTicks: 0,
    recoveryTicks: 0,
    casterDeath: 'resolve-immediately',
    // A heavier arrow that punches through a rank rather than stopping at the
    // first body. The area is the lane it clears, which is why it is a
    // parameter of the shot rather than a second shape.
    shape: {
      kind: 'projectile',
      rangeTiles: 9,
      speedTilesPerS: 9,
      pierceTargets: 3,
    },
    payloads: [{ kind: 'damage', weaponPercent: 90, flat: 3, damageType: 'physical' }],
    presentation: {
      name: 'Volley',
      clip: { kind: 'fixed', clipId: 'shoot-bow' },
      effectIds: ['hero-volley'],
      projectile: 'arrow',
    },
    tags: ['archer', 'ranged', 'area'],
    incompatibleWith: [],
  },
  {
    id: 'archer-snipe',
    casters: ['player'],
    costs: { mana: 12 },
    cooldownTicks: 40,
    counterplay: 'projectile',
    // A full second of draw, then recovery. The Archer's own commitment, and
    // the first authored content that spends time before a shot exists.
    castTicks: 20,
    recoveryTicks: 6,
    // Killed mid-draw is a shot that never happened.
    casterDeath: 'cancel',
    shape: { kind: 'projectile', rangeTiles: 16, speedTilesPerS: 12 },
    payloads: [{ kind: 'damage', weaponPercent: 220, flat: 10, damageType: 'physical' }],
    presentation: {
      name: 'Snipe',
      clip: { kind: 'fixed', clipId: 'shoot-crossbow' },
      effectIds: ['hero-snipe'],
      projectile: 'bolt',
    },
    tags: ['archer', 'ranged'],
    incompatibleWith: [],
  },
  {
    id: 'archer-disengage',
    casters: ['player'],
    costs: { mana: 0 },
    cooldownTicks: 40,
    counterplay: 'instant',
    castTicks: 0,
    recoveryTicks: 0,
    casterDeath: 'resolve-immediately',
    // No payloads at all: the traversal is the ability. The Archer rolls where
    // it aimed and hurts nobody on the way.
    shape: {
      kind: 'dash',
      distanceTiles: 6,
      durationMs: 200,
      contactRadiusTiles: 0.7,
    },
    payloads: [],
    presentation: { name: 'Disengage', effectIds: ['hero-disengage'] },
    tags: ['archer', 'mobility'],
    incompatibleWith: [],
  },
  {
    id: 'archer-snare-trap',
    casters: ['player'],
    costs: { mana: 6 },
    cooldownTicks: 50,
    counterplay: 'instant',
    castTicks: 0,
    recoveryTicks: 0,
    casterDeath: 'resolve-immediately',
    // Placed at the aimed point rather than measured from the caster, which is
    // what the `tile` shape is for and what nothing has asked of it until now.
    shape: { kind: 'tile', radiusTiles: 2 },
    payloads: [
      {
        kind: 'applyStatus',
        statusId: 'slow',
        durationTicks: 60,
        magnitude: 40,
      },
    ],
    presentation: { name: 'Snare Trap', effectIds: ['hero-trap'] },
    tags: ['archer', 'control'],
    incompatibleWith: [],
  },

  // The Mage (PRD #800). The first content consumer of the cast lifecycle and
  // of typed elemental damage: three of the wheel's four elements are read here,
  // and each one resists against its own entry on the target's derived record.
  {
    id: 'mage-magic-arrow',
    casters: ['player'],
    costs: { mana: 6 },
    cooldownTicks: 10,
    counterplay: 'projectile',
    // Short but real: the bolt exists after the wind-up, not during it, so a
    // Mage under pressure feels every cast it starts.
    castTicks: 6,
    recoveryTicks: 2,
    casterDeath: 'cancel',
    shape: { kind: 'projectile', rangeTiles: 11, speedTilesPerS: 11 },
    // Typed gameplay arrives with the elemental statuses (PRD #804): the fire
    // bolt now leaves fire behind. The status rides the declaration, so the
    // Mage's mechanics moved without the Mage moving.
    payloads: [
      { kind: 'damage', weaponPercent: 120, flat: 5, damageType: 'fire' },
      { kind: 'applyStatus', statusId: 'burning', durationTicks: 40, magnitude: 3 },
    ],
    presentation: {
      name: 'Magic Arrow',
      clip: { kind: 'fixed', clipId: 'cast' },
      effectIds: ['hero-bolt'],
      projectile: 'magic-arrow',
    },
    tags: ['mage', 'ranged', 'fire'],
    incompatibleWith: [],
  },
  {
    id: 'mage-lightning',
    casters: ['player'],
    costs: { mana: 12 },
    cooldownTicks: 30,
    // A bolt down a lane cannot be dodged once it exists, so the answer is the
    // wind-up: the lane is announced before anything happens in it.
    counterplay: 'telegraphed',
    castTicks: 12,
    recoveryTicks: 4,
    casterDeath: 'cancel',
    shape: { kind: 'line', rangeTiles: 8, widthTiles: 1.2 },
    payloads: [{ kind: 'damage', weaponPercent: 200, flat: 12, damageType: 'energy' }],
    presentation: {
      name: 'Lightning',
      clip: { kind: 'fixed', clipId: 'cast' },
      effectIds: ['hero-lightning'],
    },
    tags: ['mage', 'energy'],
    incompatibleWith: [],
  },
  {
    id: 'mage-nova',
    casters: ['player'],
    costs: { mana: 18 },
    cooldownTicks: 45,
    counterplay: 'telegraphed',
    castTicks: 10,
    recoveryTicks: 6,
    casterDeath: 'cancel',
    shape: { kind: 'circle', radiusTiles: 4, metric: 'euclidean' },
    // Cold takes movement now (PRD #804): everything the ring catches walks
    // away at two thirds speed, which makes Nova a decision about position
    // rather than only a number.
    payloads: [
      { kind: 'damage', weaponPercent: 150, flat: 8, damageType: 'cold' },
      { kind: 'applyStatus', statusId: 'chilled', durationTicks: 60, magnitude: 35 },
    ],
    presentation: {
      name: 'Nova',
      clip: { kind: 'fixed', clipId: 'cast' },
      effectIds: ['hero-nova'],
    },
    tags: ['mage', 'area', 'cold'],
    incompatibleWith: [],
  },
  {
    id: 'mage-blink',
    casters: ['player'],
    costs: { mana: 4 },
    cooldownTicks: 35,
    counterplay: 'instant',
    // Responsive on purpose: a class that commits to every cast needs one
    // answer it does not have to commit to.
    castTicks: 0,
    recoveryTicks: 0,
    casterDeath: 'resolve-immediately',
    shape: {
      kind: 'dash',
      distanceTiles: 7,
      durationMs: 150,
      contactRadiusTiles: 0.5,
    },
    payloads: [],
    presentation: { name: 'Blink', effectIds: ['hero-blink'] },
    tags: ['mage', 'mobility'],
    incompatibleWith: [],
  },
  {
    id: 'mage-ward',
    casters: ['player'],
    costs: { mana: 10 },
    cooldownTicks: 180,
    counterplay: 'instant',
    castTicks: 0,
    recoveryTicks: 0,
    casterDeath: 'resolve-immediately',
    shape: { kind: 'self' },
    payloads: [
      {
        kind: 'applyStatus',
        statusId: 'damage-reduction',
        durationTicks: 80,
        magnitude: 35,
      },
    ],
    presentation: {
      name: 'Ward',
      clip: { kind: 'fixed', clipId: 'spell2' },
      effectIds: ['hero-ward'],
    },
    tags: ['mage', 'defensive', 'buff'],
    incompatibleWith: [],
  },

  // The Necromancer (PRD #804). The class built on attrition, debuff, and
  // command: rot on the target, a resist stripped before the rot lands, a
  // minion holding the line, and a form that changes how the caster moves.
  // The kit is worth more in an order than individually, and nothing enforces
  // the order; the numbers simply reward knowing it. First-pass throughout,
  // owned by the tuning PRD (#799).
  {
    id: 'necromancer-poison-bolt',
    casters: ['player'],
    costs: { mana: 4 },
    cooldownTicks: 10,
    // Dodged like the Archer's Quick Shot: the answer is stepping out of the
    // bolt's line, and the rot it carries is part of what is dodged.
    counterplay: 'projectile',
    castTicks: 5,
    recoveryTicks: 2,
    casterDeath: 'cancel',
    shape: { kind: 'projectile', rangeTiles: 10, speedTilesPerS: 10 },
    payloads: [
      { kind: 'damage', weaponPercent: 100, flat: 3, damageType: 'poison' },
      // The rot is the point: Poisoned stacks, so layered bolts are the class
      // doing its job rather than a reset clock.
      { kind: 'applyStatus', statusId: 'poisoned', durationTicks: 40, magnitude: 2 },
    ],
    presentation: {
      name: 'Poison Bolt',
      clip: { kind: 'fixed', clipId: 'cast' },
      effectIds: ['hero-poison-bolt'],
      projectile: 'magic-arrow',
    },
    tags: ['necromancer', 'ranged', 'poison'],
    incompatibleWith: [],
  },
  {
    id: 'necromancer-animate-dead',
    casters: ['player'],
    costs: { mana: 15 },
    cooldownTicks: 60,
    counterplay: 'instant',
    castTicks: 0,
    recoveryTicks: 0,
    casterDeath: 'resolve-immediately',
    shape: { kind: 'self' },
    // The first consumer of the summon payload: a skeleton from the shipped
    // bestiary, running the same melee-pursuer brain a monster runs, bound to
    // its owner and gone within half a second of that owner's death.
    payloads: [
      {
        kind: 'summon',
        archetypeId: 'skeleton-warrior',
        brainId: 'melee-pursuer',
        count: 1,
        lifetimeTicks: 600,
        maxAlive: 2,
        ownerDeath: 'dissipate',
      },
    ],
    presentation: {
      name: 'Animate Dead',
      clip: { kind: 'fixed', clipId: 'spell2' },
      effectIds: ['hero-animate-dead'],
    },
    tags: ['necromancer', 'summon'],
    incompatibleWith: [],
  },
  {
    id: 'necromancer-corpse-skin',
    casters: ['player'],
    costs: { mana: 8 },
    cooldownTicks: 30,
    counterplay: 'projectile',
    castTicks: 5,
    recoveryTicks: 2,
    casterDeath: 'cancel',
    shape: { kind: 'projectile', rangeTiles: 10, speedTilesPerS: 10 },
    // No damage at all. It strips Poison and Fire resist for ten seconds,
    // which is the setup that makes everything else in the kit land harder,
    // and casting it first is the sequence the class rewards.
    payloads: [
      { kind: 'applyStatus', statusId: 'corpse-skin', durationTicks: 200, magnitude: 25 },
    ],
    presentation: {
      name: 'Corpse Skin',
      clip: { kind: 'fixed', clipId: 'cast' },
      effectIds: ['hero-corpse-skin'],
      projectile: 'magic-arrow',
    },
    tags: ['necromancer', 'debuff', 'poison'],
    incompatibleWith: [],
  },
  {
    id: 'necromancer-wither',
    casters: ['player'],
    costs: { mana: 16 },
    cooldownTicks: 45,
    counterplay: 'instant',
    castTicks: 0,
    recoveryTicks: 0,
    casterDeath: 'resolve-immediately',
    // The panic button and the area slot in one: everything around the caster,
    // right now, typed into the element Corpse Skin just stripped.
    shape: { kind: 'circle', radiusTiles: 3, metric: 'euclidean' },
    payloads: [{ kind: 'damage', weaponPercent: 160, flat: 8, damageType: 'poison' }],
    presentation: {
      name: 'Wither',
      clip: { kind: 'fixed', clipId: 'spell2' },
      effectIds: ['hero-wither'],
    },
    tags: ['necromancer', 'area', 'poison'],
    incompatibleWith: [],
  },
  {
    id: 'necromancer-wraith-form',
    casters: ['player'],
    costs: { mana: 10 },
    cooldownTicks: 200,
    counterplay: 'instant',
    castTicks: 0,
    recoveryTicks: 0,
    casterDeath: 'resolve-immediately',
    shape: { kind: 'self' },
    // The defensive slot that is not a shield: faster and harder to hurt, so
    // the answer to pressure is movement rather than standing in it.
    payloads: [
      { kind: 'applyStatus', statusId: 'wraith-form', durationTicks: 160, magnitude: 30 },
    ],
    presentation: {
      name: 'Wraith Form',
      clip: { kind: 'fixed', clipId: 'spell2' },
      effectIds: ['hero-wraith-form'],
    },
    tags: ['necromancer', 'defensive', 'buff', 'mobility'],
    incompatibleWith: [],
  },

  // The monster catalog (PRD #793). Every entry here is castable by a monster
  // and by nothing else, so every damaging one is bound by the counterplay law
  // and had to declare how it can be answered before it could exist.
  //
  // Clips are the bestiary action set and nothing else: a creature body carries
  // walk, stand, die, and three attack poses, and has no cast or draw pose to
  // play. A role is therefore expressed in the ability's geometry and timing
  // rather than in art the bodies do not have, which is what let this land
  // without an art budget.
  {
    id: 'monster-strike',
    casters: ['monster'],
    costs: { mana: 0 },
    // Twenty ticks is exactly the cadence the dungeon's hand-applied swing kept
    // in a run-state map, so the skeleton that moved onto this entry swings at
    // the same speed it always has.
    cooldownTicks: 20,
    counterplay: 'melee',
    castTicks: 0,
    recoveryTicks: 0,
    casterDeath: 'resolve-immediately',
    // Contact reach, not selection reach: the brain decides whether it is close
    // enough and whether it could step onto the target's cell, and the shape
    // only has to be generous enough that a body stopped at melee range is
    // inside it rather than a floating-point hair outside.
    shape: { kind: 'circle', radiusTiles: 1.4, metric: 'euclidean' },
    payloads: [{ kind: 'damage', weaponPercent: 100, flat: 0, damageType: 'physical' }],
    presentation: {
      name: 'Strike',
      clip: { kind: 'fixed', clipId: 'attack-a' },
      effectIds: ['monster-swing'],
    },
    tags: ['monster', 'melee'],
    incompatibleWith: [],
  },
  {
    id: 'monster-arrow',
    casters: ['monster'],
    costs: { mana: 0 },
    cooldownTicks: 26,
    counterplay: 'projectile',
    // A visible draw before the shot exists, so the answer to an archer is
    // reading the pose and moving, not reacting to an arrow already in the air.
    castTicks: 8,
    recoveryTicks: 4,
    casterDeath: 'cancel',
    shape: { kind: 'projectile', rangeTiles: 9, speedTilesPerS: 10 },
    payloads: [{ kind: 'damage', weaponPercent: 100, flat: 0, damageType: 'physical' }],
    presentation: {
      name: 'Arrow',
      clip: { kind: 'fixed', clipId: 'attack-b' },
      effectIds: ['monster-shot'],
      projectile: 'arrow',
    },
    tags: ['monster', 'ranged'],
    incompatibleWith: [],
  },
  {
    id: 'monster-bolt',
    casters: ['monster'],
    costs: { mana: 0 },
    cooldownTicks: 24,
    counterplay: 'projectile',
    castTicks: 6,
    recoveryTicks: 2,
    casterDeath: 'cancel',
    shape: { kind: 'projectile', rangeTiles: 8, speedTilesPerS: 9 },
    payloads: [{ kind: 'damage', weaponPercent: 70, flat: 0, damageType: 'energy' }],
    presentation: {
      name: 'Bolt',
      clip: { kind: 'fixed', clipId: 'attack-c' },
      effectIds: ['monster-arcane'],
      projectile: 'magic-arrow',
    },
    tags: ['monster', 'ranged', 'energy'],
    incompatibleWith: [],
  },
  {
    id: 'geyser',
    casters: ['monster'],
    costs: { mana: 0 },
    cooldownTicks: 70,
    counterplay: 'telegraphed',
    // A full second of warning on a footprint the player can read and walk out
    // of. The one entry in this block adopted end to end, on the Ratman Mage.
    castTicks: 20,
    recoveryTicks: 6,
    // A committed hazard is committed: killing the caster mid-wind-up does not
    // un-announce an area the player has already been told to leave.
    casterDeath: 'persist',
    shape: { kind: 'tile', radiusTiles: 2 },
    payloads: [{ kind: 'damage', weaponPercent: 130, flat: 0, damageType: 'cold' }],
    presentation: {
      name: 'Geyser',
      clip: { kind: 'fixed', clipId: 'attack-c' },
      effectIds: ['monster-geyser'],
    },
    tags: ['monster', 'area', 'cold'],
    incompatibleWith: [],
  },
  {
    id: 'monster-charge',
    casters: ['monster'],
    costs: { mana: 0 },
    cooldownTicks: 90,
    // A rush that crosses six tiles cannot be answered by proximity, so the
    // answer is the wind-up: the lane is announced before anything moves down
    // it. The Warrior's own Charge is instant and player-only for exactly the
    // reason this one is not.
    counterplay: 'telegraphed',
    castTicks: 12,
    recoveryTicks: 8,
    casterDeath: 'cancel',
    shape: { kind: 'dash', distanceTiles: 6, durationMs: 260, contactRadiusTiles: 0.8 },
    payloads: [
      { kind: 'damage', weaponPercent: 140, flat: 0, damageType: 'physical' },
      { kind: 'knockback', distanceTiles: 1, direction: 'away' },
    ],
    presentation: {
      name: 'Charge',
      clip: { kind: 'fixed', clipId: 'attack-a' },
      effectIds: ['monster-rush'],
    },
    tags: ['monster', 'melee', 'mobility'],
    incompatibleWith: [],
  },
  {
    id: 'walking-bomb',
    casters: ['monster'],
    costs: { mana: 0 },
    cooldownTicks: 200,
    counterplay: 'telegraphed',
    // A two-second fuse. The blast is anchored where the fuse was lit rather
    // than on the walking body, because aim freezes at cast start and a
    // footprint that followed its caster would be an area nobody could leave.
    castTicks: 40,
    recoveryTicks: 0,
    // Killing the bomb sets it off, which is the death trigger expressed as the
    // caster-death policy the kernel already owns.
    casterDeath: 'resolve-immediately',
    shape: { kind: 'circle', radiusTiles: 3, metric: 'euclidean' },
    payloads: [{ kind: 'damage', weaponPercent: 180, flat: 0, damageType: 'fire' }],
    presentation: {
      name: 'Walking Bomb',
      clip: { kind: 'fixed', clipId: 'attack-a' },
      effectIds: ['monster-bomb'],
    },
    tags: ['monster', 'area', 'fire'],
    incompatibleWith: [],
  },
  {
    id: 'web',
    casters: ['monster'],
    costs: { mana: 0 },
    cooldownTicks: 120,
    counterplay: 'telegraphed',
    castTicks: 12,
    recoveryTicks: 4,
    casterDeath: 'persist',
    shape: { kind: 'tile', radiusTiles: 2 },
    // The one non-damaging entry here: it takes movement rather than health,
    // and the `slow` registry entry restores what it took when it expires.
    payloads: [
      { kind: 'applyStatus', statusId: 'slow', durationTicks: 60, magnitude: 45 },
    ],
    presentation: {
      name: 'Web',
      clip: { kind: 'fixed', clipId: 'attack-c' },
      effectIds: ['monster-web'],
    },
    tags: ['monster', 'control'],
    incompatibleWith: [],
  },
  {
    id: 'meteor-strike',
    casters: ['monster'],
    costs: { mana: 0 },
    cooldownTicks: 140,
    counterplay: 'telegraphed',
    castTicks: 24,
    recoveryTicks: 8,
    casterDeath: 'persist',
    // The same `tile` shape Geyser uses, tuned differently and typed
    // differently. Two entries rather than one parameterised entry, because a
    // rebalance of falling fire should not move a column of water.
    shape: { kind: 'tile', radiusTiles: 2 },
    payloads: [{ kind: 'damage', weaponPercent: 160, flat: 0, damageType: 'fire' }],
    presentation: {
      name: 'Meteor Strike',
      clip: { kind: 'fixed', clipId: 'attack-c' },
      effectIds: ['monster-meteor'],
    },
    tags: ['monster', 'area', 'fire'],
    incompatibleWith: [],
  },
  {
    id: 'meteor-shower',
    casters: ['monster'],
    costs: { mana: 0 },
    cooldownTicks: 240,
    counterplay: 'telegraphed',
    castTicks: 20,
    recoveryTicks: 10,
    casterDeath: 'persist',
    // Five impacts, frozen and announced together at cast start, landing one
    // after another. Nothing re-aims: the pattern is rolled once, from the
    // world rng, and every footprint is on screen before the first one lands.
    shape: {
      kind: 'tile',
      radiusTiles: 1.5,
      impacts: { count: 5, spreadTiles: 4, staggerTicks: 6 },
    },
    payloads: [{ kind: 'damage', weaponPercent: 90, flat: 0, damageType: 'fire' }],
    presentation: {
      name: 'Meteor Shower',
      clip: { kind: 'fixed', clipId: 'attack-c' },
      effectIds: ['monster-meteor'],
    },
    tags: ['monster', 'area', 'fire'],
    incompatibleWith: [],
  },
];

function mechanicalView(authored: AuthoredAbility): AbilityDefinition {
  const { presentation, ...definition } = authored;
  // The descriptor is dropped on purpose: mechanics must not see it.
  void presentation;
  return deepFreeze(definition);
}

/**
 * The one registry, keyed by stable ability id and frozen at module load. It
 * hands out mechanical definitions only.
 */
export const abilityCatalog: AbilityCatalog = new Map(
  AUTHORED_ABILITIES.map((authored) => [authored.id, mechanicalView(authored)]),
);

const PRESENTATIONS: ReadonlyMap<AbilityId, AbilityPresentation> = new Map(
  AUTHORED_ABILITIES.map((authored) => [
    authored.id,
    deepFreeze({ ...authored.presentation }),
  ]),
);

/** Look up an ability's mechanical definition. This is the primary accessor. */
export function ability(id: AbilityId): AbilityDefinition | null {
  return abilityCatalog.get(id) ?? null;
}

/**
 * Look up an ability's presentation descriptor.
 *
 * Only the wire and client edge may call this: the protocol encoder that ships
 * a cast to the client, and the client code that turns a descriptor into clips
 * and effects. No simulation module, and in particular nothing under
 * `src/sim/combat/`, may name this function. The import guard in
 * `abilityCatalog.test.ts` holds the allowed-caller list.
 */
export function abilityPresentation(id: AbilityId): AbilityPresentation | null {
  return PRESENTATIONS.get(id) ?? null;
}

/**
 * One entry of a combatant's base abilities.
 *
 * `unlockLevel` lives here rather than on the catalog entry because unlocking
 * is progression data, not ability data: the same catalog entry can be a
 * level-one Warrior skill, a level-thirty one for another class, and a monster
 * ability with no notion of level at all, so the gate belongs to the loadout
 * that grants it.
 */
export interface LoadoutBaseEntry {
  abilityId: AbilityId;
  /** The level at which this loadout unlocks the ability. Defaults to 1. */
  unlockLevel?: number;
}

/** One affix's ability grants. Affix ids are unique within a composition. */
export interface AffixGrant {
  affixId: string;
  abilityIds: readonly AbilityId[];
}

export interface ComposeLoadoutOptions {
  /**
   * The combatant's level. When given, base entries whose `unlockLevel` exceeds
   * it are left out. Affix grants are never level gated: an affix grants its
   * ability outright.
   */
  level?: number;
  catalog?: AbilityCatalog;
}

export type LoadoutComposition =
  | { ok: true; loadout: readonly AbilityId[] }
  | {
      ok: false;
      reason:
        | 'unknown-ability'
        | 'duplicate-affix'
        | 'incompatible'
        | 'invalid-unlock-level'
        | 'invalid-level';
      detail: string;
    };

/**
 * Compose an evaluation-ordered loadout from base abilities plus affix grants.
 *
 * Pure and stable: base entries keep their order, grants follow in affix order,
 * and a repeated id keeps its first position. Unknown ids, duplicate affix ids,
 * and declared incompatibilities are rejected with distinct reasons. An unknown
 * id is rejected even when the level gate would have dropped it, so a typo in
 * a locked entry cannot hide until the level-up.
 *
 * Levels are validated rather than compared blind: `NaN > 1` is `false`, so an
 * unchecked malformed level would quietly unlock everything it should gate.
 */
export function composeLoadout(
  base: readonly LoadoutBaseEntry[],
  affixGrants: readonly AffixGrant[] = [],
  options: ComposeLoadoutOptions = {},
): LoadoutComposition {
  const catalog = options.catalog ?? abilityCatalog;
  const isLevel = (value: unknown): boolean =>
    Number.isSafeInteger(value) && (value as number) >= 1;
  if (options.level !== undefined && !isLevel(options.level)) {
    return { ok: false, reason: 'invalid-level', detail: String(options.level) };
  }
  const loadout: AbilityId[] = [];
  const seen = new Set<AbilityId>();
  const seenAffixes = new Set<string>();

  const add = (id: AbilityId): LoadoutComposition | null => {
    const definition = catalog.get(id);
    if (!definition) return { ok: false, reason: 'unknown-ability', detail: id };
    if (seen.has(id)) return null;
    for (const present of loadout) {
      const other = catalog.get(present)!;
      if (
        definition.incompatibleWith.includes(present) ||
        other.incompatibleWith.includes(id)
      ) {
        return { ok: false, reason: 'incompatible', detail: `${present}|${id}` };
      }
    }
    seen.add(id);
    loadout.push(id);
    return null;
  };

  for (const entry of base) {
    if (!catalog.has(entry.abilityId)) {
      return { ok: false, reason: 'unknown-ability', detail: entry.abilityId };
    }
    if (entry.unlockLevel !== undefined && !isLevel(entry.unlockLevel)) {
      return {
        ok: false,
        reason: 'invalid-unlock-level',
        detail: `${entry.abilityId}|${String(entry.unlockLevel)}`,
      };
    }
    if (options.level !== undefined && (entry.unlockLevel ?? 1) > options.level) {
      continue;
    }
    const failure = add(entry.abilityId);
    if (failure) return failure;
  }
  for (const grant of affixGrants) {
    if (seenAffixes.has(grant.affixId)) {
      return { ok: false, reason: 'duplicate-affix', detail: grant.affixId };
    }
    seenAffixes.add(grant.affixId);
    for (const id of grant.abilityIds) {
      const failure = add(id);
      if (failure) return failure;
    }
  }
  return { ok: true, loadout };
}
